import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, getUserRole } from '../api';

/**
 * Floating tutor selector for admin users in the chat interface.
 * Allows admins to select a tutor and filter chat context to only
 * that tutor's assigned students and sessions.
 */
const TutorSelector = ({ onTutorSelect, selectedTutor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Check role - must be before hooks for consistent ordering
  const userRole = getUserRole();
  const isAdmin = userRole === 'admin';

  // Fetch tutors on mount (only for admins)
  useEffect(() => {
    if (!isAdmin) return;

    const loadTutors = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch('/api/chat/tutors');
        if (!response.ok) {
          throw new Error('Failed to fetch tutors');
        }
        const data = await response.json();
        setTutors(data.tutors || []);
      } catch (err) {
        console.error('Error fetching tutors:', err);
        setError('Could not load tutors');
      } finally {
        setLoading(false);
      }
    };

    loadTutors();
  }, [isAdmin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isAdmin) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAdmin]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Refetch function for retry button
  const fetchTutors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/chat/tutors');
      if (!response.ok) {
        throw new Error('Failed to fetch tutors');
      }
      const data = await response.json();
      setTutors(data.tutors || []);
    } catch (err) {
      console.error('Error fetching tutors:', err);
      setError('Could not load tutors');
    } finally {
      setLoading(false);
    }
  };

  // Only render for admin users (after all hooks)
  if (!isAdmin) {
    return null;
  }

  const handleTutorSelect = (tutor) => {
    onTutorSelect(tutor);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onTutorSelect(null);
    setSearchQuery('');
  };

  // Filter tutors based on search query
  const filteredTutors = tutors.filter(tutor => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tutor.name.toLowerCase().includes(query) ||
      (tutor.email && tutor.email.toLowerCase().includes(query))
    );
  });

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <div className="tutor-selector-container" ref={dropdownRef}>
      {/* Floating Icon Button */}
      <button
        className={`tutor-selector-trigger ${selectedTutor ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={selectedTutor ? `Viewing as: ${selectedTutor.name}` : 'Select tutor to filter'}
        aria-label="Select tutor"
        aria-expanded={isOpen}
      >
        <span className="tutor-icon">
          {selectedTutor ? getInitials(selectedTutor.name) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          )}
        </span>
        {selectedTutor && <span className="tutor-indicator" />}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="tutor-selector-dropdown">
          <div className="dropdown-header">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search tutors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tutor-search-input"
            />
            {selectedTutor && (
              <button
                className="clear-filter-btn"
                onClick={handleClear}
                title="Clear filter"
              >
                Clear
              </button>
            )}
          </div>

          <div className="dropdown-list">
            {loading && (
              <div className="dropdown-loading">Loading tutors...</div>
            )}

            {error && (
              <div className="dropdown-error">
                {error}
                <button onClick={fetchTutors} className="retry-btn">Retry</button>
              </div>
            )}

            {!loading && !error && filteredTutors.length === 0 && (
              <div className="dropdown-empty">
                {searchQuery ? 'No tutors match your search' : 'No tutors available'}
              </div>
            )}

            {!loading && !error && filteredTutors.map(tutor => (
              <div
                key={tutor.id}
                className={`tutor-option ${selectedTutor?.id === tutor.id ? 'selected' : ''}`}
                onClick={() => handleTutorSelect(tutor)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleTutorSelect(tutor)}
              >
                <span className="tutor-avatar">
                  {getInitials(tutor.name)}
                </span>
                <div className="tutor-info">
                  <span className="tutor-name">{tutor.name}</span>
                  <span className="tutor-meta">
                    {tutor.student_count} student{tutor.student_count !== 1 ? 's' : ''}
                  </span>
                </div>
                {selectedTutor?.id === tutor.id && (
                  <span className="check-icon">✓</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorSelector;
